import { useWindowDimensions } from "react-native";
import { PressableScale } from "pressto";
import { View, Text } from "@src/components";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { Image } from "expo-image";
import { Icon } from "@src/components/Icon";
import { IconButton } from "@src/components/IconButton";
import { memo, type FC, useMemo } from "react";
import { FeatureGate } from "@src/services/feature-flags/FeatureGate";
import { FEATURE_FLAGS } from "@src/services/feature-flags/feature-flags";
import { useSnapshotState } from "./useSnapshotState";

type CameraSnapshotProps = {
  snapshotUrl: string | null;
  snapshotCapturedAt?: string | null;
  onPlayPress: () => void;
  isOnline?: boolean;
  cameraKey?: string;
};

const SIZE_DOT_ICON = 10;
const SIZE_PLAY_ICON = 14;

export const CameraSnapshot: FC<CameraSnapshotProps> = memo(
  ({ snapshotUrl, snapshotCapturedAt, onPlayPress, isOnline = false, cameraKey }) => {
    const { theme } = useUnistyles();
    const { width: screenWidth, height: screenHeight } = useWindowDimensions();
    const { snapshotError, setSnapshotError, chipState } = useSnapshotState({
      snapshotUrl,
      snapshotCapturedAt,
      isOnline,
      cameraKey,
    });

    const containerWidth = screenWidth - theme.spacing["5xl"] * 2;
    const containerHeight = screenHeight * 0.45;

    // Always use the latest snapshot URL from API
    // expo-image will handle caching automatically by URL
    const imageSource = snapshotUrl || "";
    const shouldShowImage = imageSource && !snapshotError;
    const snapshotCacheKey =
      snapshotCapturedAt && cameraKey ? `${cameraKey}:${snapshotCapturedAt}`
      : cameraKey ? `${cameraKey}:${Math.floor(Date.now() / (5 * 60 * 1000))}`
      : `${Math.floor(Date.now() / (5 * 60 * 1000))}`;

    const { chipLabel, showSnapshotChip } = chipState;

    const chipBgColor =
      snapshotCapturedAt ? theme.colors.mediaOverlay.backgroundMedium
      : isOnline ? theme.colors.statusBadge.liveBackground
      : theme.colors.statusBadge.offlineBackground;

    return (
      <View
        style={[
          styles.container,
          {
            width: containerWidth,
            height: containerHeight,
          },
        ]}
      >
        <PressableScale
          onPress={onPlayPress}
          accessibilityRole="button"
          accessibilityLabel="Play livestream"
          style={styles.imagePressable}
        >
          {shouldShowImage ?
            <Image
              source={{ uri: imageSource, cacheKey: snapshotCacheKey } as any}
              style={[styles.image, styles.imageZoom]}
              contentFit="cover"
              cachePolicy="memory-disk"
              onError={() => setSnapshotError(true)}
            />
          : <View style={[styles.image, { backgroundColor: theme.colors.muted }]} />}
        </PressableScale>
        {showSnapshotChip && (
          <FeatureGate flag={FEATURE_FLAGS.CAMERA_DETAILS_LIVE_CHIP}>
            <View style={[styles.liveIndicator, { backgroundColor: chipBgColor }]}>
              {!snapshotCapturedAt && <Icon name="dot" size={SIZE_DOT_ICON} color={theme.colors.white.default} />}
              <Text textStyle="xs/medium" style={{ color: theme.colors.white.default }}>
                {chipLabel}
              </Text>
            </View>
          </FeatureGate>
        )}
        <View style={styles.playButton}>
          <IconButton
            Icon={() => <Icon name="play" size={SIZE_PLAY_ICON} color={theme.colors.text} />}
            size="sm"
            title="Play"
            onPress={onPlayPress}
          />
        </View>
      </View>
    );
  },
);

const styles = StyleSheet.create((theme) => ({
  container: {
    alignSelf: "center",
    borderRadius: theme.spacing.md,
    borderCurve: "continuous",
    overflow: "hidden",
  },
  image: {
    width: "100%",
    height: "100%",
  },
  imageZoom: {
    transform: [{ scale: 1.2 }],
  },
  imagePressable: {
    width: "100%",
    height: "100%",
  },
  liveIndicator: {
    position: "absolute",
    top: theme.spacing.xl,
    left: theme.spacing.xl,
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.xxs,
    borderRadius: theme.spacing["3xl"],
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: theme.spacing.xxs,
  },
  playButton: {
    position: "absolute",
    bottom: theme.spacing.md,
    right: theme.spacing.md,
  },
}));
